/**
 * "Retry delivery" gate for a catalog batch — one rule set shared by the button
 * (checks before navigating, shows a popup when blocked) and the redeliver
 * wizard (defends a deep link), so the two never disagree.
 *
 * Redelivery is allowed from ANY finished run of the batch: completed, failed,
 * or partially delivered (e.g. a run that stopped half-way with some products
 * NotFound). It is blocked only while a delivery is genuinely still moving.
 * The batch row's approval state is deliberately NOT consulted: a failed
 * delivery can leave it back at AWAITING_APPROVAL with no linked run id even
 * though integration-service holds a finished run for it.
 */
import type { DeliveryRunDetail } from '../types/delivery-runs.types';

export type RedeliveryBlockReason = 'membersOnly' | 'runInFlight' | 'batchDelivering';

/** The two batch-header fields the gate reads. */
export interface BatchDeliveryState {
  isDelivering: boolean;
  deliveryJobId?: string | null | undefined;
}

interface GateInput {
  isTenantAdmin: boolean;
  run: Pick<DeliveryRunDetail, 'id' | 'isTerminal'>;
  batch: BatchDeliveryState;
}

/** Why "Retry delivery" can't start from `run` right now, or `null` when it can. */
export function redeliveryBlockReason({
  isTenantAdmin,
  run,
  batch,
}: GateInput): RedeliveryBlockReason | null {
  if (!isTenantAdmin) return 'membersOnly';
  if (!run.isTerminal) return 'runInFlight';
  // A DELIVERING batch whose linked run is the one being retried is a stale settle
  // (that run already finished), not a live delivery — don't block on it.
  if (batch.isDelivering && batch.deliveryJobId !== run.id) return 'batchDelivering';
  return null;
}

/**
 * `forceRedeliver` for a redelivery started from `run`. Only a run that delivered
 * everything cleanly is re-pushed in full; from a failed or partial run the retry
 * resumes — products already delivered with an unchanged payload are skipped, so
 * the never-attempted and failed ones go out without re-sending the rest.
 * Changing the attribute selection still re-sends (the payload hash differs).
 *
 * "Cleanly" means nothing failed AND nothing failed validation — `validationFailed`
 * is its own bucket, disjoint from `failed`, so a COMPLETED run can carry it with
 * `failed === 0`. Unchanged skips (`skippedUnchanged`) don't count against it.
 */
export function shouldForceRedeliver(run: Pick<DeliveryRunDetail, 'phase' | 'totals'>): boolean {
  const { failed, validationFailed = 0 } = run.totals;
  return run.phase === 'COMPLETED' && failed === 0 && validationFailed === 0;
}
